import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { CloudUploadOutlined, LoadingOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Empty,
  Input,
  message,
  Modal,
  Rate,
  Row,
  Upload,
  Image,
  Card,
  Avatar,
} from 'antd';
import type { UploadProps } from 'antd/es/upload/interface';
import { createSearchParams, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { UserOutlined } from '@ant-design/icons';

import styles from './index.module.scss';

import { sendNotiMessage } from '../../../../reducers/actions/orderService';

const ORDER_SERVICE_JOB_TYPE = 'owner';

interface OrderService {
  _id: string;
  user: Object;
  applyJob: {
    idCard: string;
    technician: {
      name: string;
      phone: string;
      address: string;
      district: string;
      subDistrict: string;
      postalCode: string;
      subDistrictName: string;
      districtName: string;
      provinceName: string;
    };
  };
  description: string;
  customer: Object;
  orderNo: string;
  images: Object[];
  category: keyof typeof CATEGORY;
  status: string;
  updatedAt: string;
  createdAt: string;
}

interface selectedOrderService {
  orderNo: string;
  category: keyof typeof CATEGORY;
  applyJob: {
    user: string;
  };
  status: string;
  customer: {
    name: string;
    phone: string;
    address: string;
    subDistrictName: string;
    districtName: string;
    provinceName: string;
    postCode: string;
  };
  description: string;
  images: Object[];
}

enum CATEGORY {
  'electrical' = 'ระบบไฟฟ้า',
  'water' = 'ระบบประปา',
  'floor' = 'งานปูน/พื้น/กระเบื้อง',
  'aluminium' = 'งานเหล็ก/อลูมิเนียม/ไม้',
  'ceiling' = 'งานฝ้า/หลังคา',
  'paint' = 'งานทาสี',
  'contractor' = 'ผู้รับเหมา',
  'architect' = 'ออกแบบ/สถาปนิก',
}

enum STATUS {
  'CREATED' = 'CREATED',
  'ACCEPT' = 'ACCEPT',
  'REJECT' = 'REJECT',
  'CANCEL' = 'CANCEL',
}

interface Props {}

const MyServiceJobs: React.FC<Props> = (props) => {
  const navigate = useNavigate();
  const useQuery = () => {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  };
  const query = useQuery();

  const [orders, setOrders] = useState<OrderService[]>([]);
  const [page, setPage] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [confirmCancelModal, setConfirmCancelModal] = useState<boolean>(false);
  const [selectedJob, setSelectedJob] = useState<selectedOrderService>();
  const [detailJobModal, setDetailJobModal] = useState<boolean>(false);

  const status = query.get('status') as STATUS;
  const statusBtns = [
    {
      status: STATUS.CREATED,
      label: 'งานที่ติดต่อช่าง',
    },
    {
      status: STATUS.ACCEPT,
      label: 'งานที่ช่างตอบรับ',
    },
    {
      status: STATUS.REJECT,
      label: 'งานที่ช่างไม่ตอบรับ',
    },
    {
      status: STATUS.CANCEL,
      label: 'งานที่ยกเลิกแล้ว',
    },
  ];

  const getOrders = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`/order-service-jobs`, {
        params: {
          page,
          type: ORDER_SERVICE_JOB_TYPE,
          status,
        },
      });
      setOrders(data?.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const redirectWithQuery = (query: STATUS) => {
    const params = createSearchParams({
      status: query,
    });
    navigate({
      pathname: window.location.pathname,
      search: `?${params}`,
    });
  };

  const handleConfirmCancelModalClick = (order: any) => {
    setSelectedJob(order);
    setConfirmCancelModal(true);
  };

  const handleJobDetailModalClick = (order: any) => {
    setSelectedJob(order);
    setDetailJobModal(true);
  };

  const cancelServiceJob = async (
    orderNo: string,
    userIdTechnician: string
  ) => {
    try {
      const res = await axios.patch(`/order-service-jobs/${orderNo}`, {
        status: 'CANCEL',
      });
      const { status } = res;
      if (status === 200) {
        message.success('คำสั่งซื้อบริการช่างถูกยกเลิกแล้ว');
        sendNotiMessage('cancel', userIdTechnician, orderNo);
        getOrders();
      }
    } catch (error) {
      message.error('คำสั่งซื้อบริการช่างยกเลิกผิดพลาด กรุณาลองใหม่อีกครั้ง');
    }
    setConfirmCancelModal(false);
  };

  useEffect(() => {
    if (!status) {
      redirectWithQuery(STATUS.CREATED);
      return;
    }
  }, []);

  useEffect(() => {
    if (status) getOrders();
  }, [status]);

  const FilterButtons = () => {
    return (
      <div className={styles.filterBtnContainer}>
        {statusBtns.map((btn) => (
          <Button
            className={[
              styles.filterBtn,
              status === btn.status ? styles.active : '',
            ].join(' ')}
            shape='round'
            onClick={() => redirectWithQuery(btn.status)}
          >
            {btn.label}
          </Button>
        ))}
      </div>
    );
  };

  const ConfirmCancelModal = () => {
    return (
      <>
        <Modal
          visible={confirmCancelModal}
          onCancel={() => setConfirmCancelModal(false)}
          title='ยกเลิกงาน'
          footer={[
            <>
              <Button onClick={() => setConfirmCancelModal(false)}>ไม่</Button>
              <Button
                type='primary'
                onClick={() =>
                  cancelServiceJob(
                    selectedJob?.orderNo!,
                    selectedJob?.applyJob.user!
                  )
                }
              >
                ใช่
              </Button>
            </>,
          ]}
        >
          <p>
            ยืนยัน ต้องการยกเลิกคำสั่งซื้อบริการช่าง {selectedJob?.orderNo}{' '}
            ใช่หรือไม่
          </p>
        </Modal>
      </>
    );
  };

  const DetailJobModal = () => {
    return (
      <>
        <Modal
          visible={detailJobModal}
          onCancel={() => setDetailJobModal(false)}
          title='รายละเอียดงาน'
          width={800}
          footer={[
            <>
              <Button onClick={() => setDetailJobModal(false)}>ปิด</Button>
            </>,
          ]}
        >
          <>
            <Card
              title={
                <Row justify='start' gutter={[32, 32]}>
                  <Col>
                    <Avatar
                      size={{
                        xs: 64,
                        sm: 64,
                        md: 64,
                        lg: 64,
                        xl: 80,
                        xxl: 100,
                      }}
                      icon={<UserOutlined />}
                    />
                  </Col>
                  <Col>
                    <h3 style={{ fontSize: '24px', textAlign: 'left' }}>
                      {selectedJob?.customer?.name}
                    </h3>
                    <span style={{ textAlign: 'left' }}>
                      {selectedJob?.customer?.phone}
                    </span>
                  </Col>
                </Row>
              }
              bordered={false}
              style={{ width: '100%' }}
            >
              <div>
                <div
                  style={{
                    fontSize: '26px',
                    fontWeight: 'bold',
                    color: '#045DE9',
                    textAlign: 'left',
                    paddingBottom: '10px',
                  }}
                >
                  <span>#{selectedJob?.orderNo} </span>
                  {selectedJob && (
                    <span style={{ color: 'black' }}>
                      {CATEGORY[selectedJob.category]}
                    </span>
                  )}
                </div>
                <div>
                  <Row
                    justify='start'
                    gutter={[8, 8]}
                    style={{
                      padding: '8px 0',
                      fontSize: '16px',
                    }}
                  >
                    <Col
                      style={{ color: '#B0B1B1', textAlign: 'right' }}
                      span={5}
                    >
                      ชื่อ - สกุลผู้ติดต่อ
                    </Col>
                    <Col>{selectedJob?.customer?.name}</Col>
                  </Row>
                  <Row
                    justify='start'
                    gutter={[8, 8]}
                    style={{
                      padding: '8px 0',
                      fontSize: '16px',
                    }}
                  >
                    <Col
                      style={{ color: '#B0B1B1', textAlign: 'right' }}
                      span={5}
                    >
                      เบอรโทรศัพท์
                    </Col>
                    <Col>{selectedJob?.customer?.phone}</Col>
                  </Row>
                  <Row
                    justify='start'
                    gutter={[8, 8]}
                    style={{
                      padding: '8px 0',
                      fontSize: '16px',
                    }}
                  >
                    <Col
                      style={{ color: '#B0B1B1', textAlign: 'right' }}
                      span={5}
                    >
                      ที่อยู่
                    </Col>
                    <Col>
                      {selectedJob?.customer?.address}{' '}
                      {`แขวง${selectedJob?.customer?.subDistrictName}`}{' '}
                      {`เขต${selectedJob?.customer?.districtName}`}{' '}
                      {`จังหวัด${selectedJob?.customer?.provinceName}`}{' '}
                      {selectedJob?.customer?.postCode}
                    </Col>
                  </Row>
                  <Row
                    justify='start'
                    gutter={[8, 8]}
                    style={{
                      padding: '8px 0',
                      fontSize: '16px',
                    }}
                  >
                    <Col
                      style={{ color: '#B0B1B1', textAlign: 'right' }}
                      span={5}
                    >
                      รายละเอียด(เพิ่มเติม)
                    </Col>
                    <Col>{selectedJob?.description}</Col>
                  </Row>
                </div>
                <br />
                <div>
                  <h2 style={{ textAlign: 'left' }}>รูปหน้างาน/แบบแปลน</h2>
                  <Image.PreviewGroup>
                    {selectedJob?.images?.map((image: any, index: number) => {
                      return (
                        <Image
                          key={index}
                          width={100}
                          height={100}
                          style={{ objectFit: 'cover', padding: '4px' }}
                          src={image.image_url}
                        />
                      );
                    })}
                  </Image.PreviewGroup>
                </div>
                <br />
                <br />
              </div>
            </Card>
          </>
        </Modal>
      </>
    );
  };

  const ActionPane = ({ order }: any) => {
    return (
      <>
        {order.status === STATUS.CREATED ? (
          <div className={styles.quotationActions}>
            <Button
              className={styles.cancelBtn}
              onClick={() => handleConfirmCancelModalClick(order)}
              danger
            >
              ยกเลิกงาน
            </Button>
          </div>
        ) : (
          ''
        )}
      </>
    );
  };

  const ServiceOrderJobs = () => {
    return (
      <>
        {orders.length ? (
          orders.map((order, i) => (
            <div className={styles.orderCard}>
              <div className={styles.subTitle}>
                <h3
                  onClick={() => handleJobDetailModalClick(order)}
                  style={{ color: '#045DE9' }}
                >
                  {order.orderNo}
                </h3>
              </div>
              <div className={styles.mainInfo}>
                <div className={styles.title}>
                  <span>{`#${i + 1}`}</span>
                  <span>{CATEGORY[order?.category]}</span>
                </div>
              </div>
              <div className={styles.cardDivider} />
              <div className={styles.cardBottom}>
                <div className={styles.technicianInfo}>
                  <img
                    className={styles.avatar}
                    src={order?.applyJob?.idCard}
                    alt=''
                  />
                  <div className={styles.name}>
                    {order.applyJob?.technician?.name}
                  </div>
                </div>
                <ActionPane order={order} />
              </div>
            </div>
          ))
        ) : (
          <>
            <Empty description='ไม่พบคำสั่งซื้อ' />
          </>
        )}
      </>
    );
  };

  return (
    <>
      <div>
        <div className={styles.header}>คำสั่งซื้อบริการช่างของฉัน</div>
        <div className={styles.divider}></div>
        <FilterButtons />
        {loading ? (
          <div className={styles.loadingContainer}>
            <LoadingOutlined />
          </div>
        ) : (
          <>
            <ServiceOrderJobs />
            <ConfirmCancelModal />
            <DetailJobModal />
          </>
        )}
      </div>
    </>
  );
};

export default MyServiceJobs;
